import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Πώς ξέρω αν είμαι έτοιμος για τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Η ετοιμότητα δεν είναι μόνο θέμα εποχής, είναι προσωπική. Αν φέρεις μια ιδέα για πάνω από 6 μήνες και εξακολουθεί να σε συγκινεί, αυτό λέει κάτι. Αν είναι σύνδεδεμένη με μια αλλαγή ζωής που έχει ήδη συμβεί, ακόμα καλύτερα." } },
    { "@type": "Question", name: "Πρέπει να περιμένω μετά από μια μεγάλη αλλαγή στη ζωή μου;", acceptedAnswer: { "@type": "Answer", text: "Εξαρτάται. Αν το τατουάζ σημαίνει την αλλαγή, είναι ένας marker που γεννήθηκε από αυτήν, τότε δεν χρειάζεται αναμονή. Αν είναι αντίδραση σε κάτι φρέσκο και συναισθηματικά έντονο, λίγος χρόνος για να «κάτσει» η απόφαση δεν βλάπτει." } },
    { "@type": "Question", name: "Τι γίνεται αν ήθελα ένα τατουάζ για χρόνια αλλά δίσταζα;", acceptedAnswer: { "@type": "Answer", text: "Αν η ιδέα επιμένει χρόνια, δεν είναι παρορμητισμός, είναι δοκιμασμένη προτίμηση. Ο δισταγμός συχνά σχετίζεται με το «πού» και «πώς», όχι με το «αν». Ένα consultation μπορεί να λύσει αυτές τις ερωτήσεις χωρίς δέσμευση." } },
  ],
};

export default function TattooTimingAthens() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Πότε είσαι έτοιμος για τατουάζ; Η προσωπική ετοιμότητα πέρα από την εποχή",
      "description": "Καριέρα, σχέσεις, σταθμοί ζωής και συναισθηματική ετοιμότητα, τα ερωτήματα που καθορίζουν αν ΕΣΥ είσαι έτοιμος για νέο τατουάζ, πέρα από εποχές και aftercare.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-04-25",
      "dateModified": "2026-04-25",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": "https://210tattoo.gr/blog/tattoo-timing-athens",
      "inLanguage": "el"
    };
    inject('schema-timing-faq', faqSchema);
    inject('schema-timing-article', articleSchema);
    return () => {
      document.getElementById('schema-timing-faq')?.remove();
      document.getElementById('schema-timing-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Πότε Είσαι Έτοιμος για Τατουάζ; Προσωπική Ετοιμότητα | 210 Tattoo Athens</title>
        <meta name="description" content="Η προσωπική ετοιμότητα για τατουάζ: καριέρα, σχέσεις, σταθμοί ζωής και συναισθηματική ωριμότητα, πέρα από εποχές και aftercare." />
        <link rel="canonical" href="https://210tattoo.gr/blog/tattoo-timing-athens" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog/tattoo-timing-athens" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog/tattoo-timing-athens" />
        <meta property="og:title" content="Πότε Είσαι Έτοιμος για Τατουάζ; Προσωπική Ετοιμότητα | 210 Tattoo Athens" />
        <meta property="og:description" content="Καριέρα, σχέσεις, αλλαγές ζωής και συναισθηματική ετοιμότητα, το άλλο είδος timing που δεν μιλάει κανείς." />
        <meta property="og:url" content="https://210tattoo.gr/blog/tattoo-timing-athens" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage('tattoo-timing-athens')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Προσωπική Ετοιμότητα για Τατουάζ | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Καριέρα, σχέσεις, σταθμοί ζωής: πότε είσαι πραγματικά έτοιμος." />
        <meta name="twitter:image" content={ogImage('tattoo-timing-athens')} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Προσωπική Ετοιμότητα για Τατουάζ</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Πότε είσαι</span>
              <br />
              <span className="text-white">έτοιμος για τατουάζ;</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">25 Απριλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Υπάρχει ένα είδος timing που δεν έχει να κάνει με εποχές ή aftercare: η προσωπική ετοιμότητα. Καριέρα, αλλαγές ζωής, σχέσεις, ηλικία, αυτά είναι τα ερωτήματα που αξίζει να κάνεις στον εαυτό σου πριν.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Tattoo as life marker */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Το τατουάζ ως <span className="text-pink-500">marker ζωής</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Πολλά από τα πιο σημαντικά τατουάζ γίνονται σε συγκεκριμένες στιγμές. Δεν είναι παρορμητισμός, είναι ότι κάποιες αλλαγές ζωής δημιουργούν ανάγκη για κάτι μόνιμο.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Αλλά ένα τατουάζ που γίνεται πριν μια αλλαγή συμβεί ή στη μέση της σύγχυσης που αυτή φέρνει, μπορεί να λέει κάτι διαφορετικό από αυτό που θέλεις να πει.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Μετά από κάτι που άλλαξε', desc: 'Ένα τατουάζ που γίνεται αφού μια αλλαγή έχει ήδη συμβεί και «κάτσει», μετακόμιση, χωρισμός, αποφοίτηση, έχει βάρος που ένα impulsive δεν έχει.' },
                { title: 'Για κάτι που θέλεις να θυμάσαι', desc: 'Δεν πρέπει να υπάρχει λόγος. Αλλά αν υπάρχει, αξίζει να είναι λόγος που δεν αλλάζει με τον καιρό, όχι συναίσθημα της στιγμής.' },
                { title: 'Πριν από κάτι που ξέρεις ότι έρχεται', desc: 'Μερικές φορές ένα τατουάζ γίνεται πριν από μια αλλαγή, ως αφετηρία, ως υπόσχεση στον εαυτό. Αυτό έχει δική του λογική αν η απόφαση έχει ωριμάσει.' },
                { title: 'Ή χωρίς κάποιο λόγο', desc: 'Δεν χρειάζεται κάθε τατουάζ να είναι marker. Μερικές φορές αρέσει και τελεία, και αυτό είναι εξίσου έγκυρο.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Career and visible placements */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Καριέρα και <span className="text-pink-500">ορατά placements</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Η επαγγελματική σου ζωή είναι ένας παράγοντας που αρκετοί υποτιμούν, και αξίζει να το σκεφτείς πρακτικά πριν αποφασίσεις για ορατό placement.
            </p>
            <div className="space-y-4">
              {[
                { title: 'Χέρια, λαιμός, πρόσωπο, πριν αλλάξεις δουλειά', desc: 'Αν είσαι σε φάση αλλαγής καριέρας ή ετοιμάζεσαι για συνεντεύξεις σε εταιρείες με αυστηρό dress code, ίσως αξίζει να γνωρίζεις το νέο περιβάλλον πρώτα.' },
                { title: 'Dress codes που αλλάζουν', desc: 'Μια δουλειά στη δημιουργική βιομηχανία έχει διαφορετικές νόρμες από έναν τραπεζικό κλάδο ή ιατρικό χώρο. Το ίδιο τατουάζ μπορεί να είναι αδιάφορο στο ένα περιβάλλον και να δημιουργεί τριβή στο άλλο.' },
                { title: 'Η ορατότητα δεν αλλάζει με cover-up', desc: 'Αντίθετα με το τι πιστεύουν αρκετοί, τα foundation και τα μακρυμάνικα δεν είναι αξιόπιστες λύσεις για κάθε περίσταση. Ένα ορατό placement είναι ορατό.' },
                { title: 'Αν το περιβάλλον δεν σε νοιάζει, προχώρα', desc: 'Αν η δουλειά σου δεν έχει τέτοιες νόρμες ή δεν σε νοιάζει, αυτό είναι εξίσου έγκυρη απόφαση. Το ερώτημα δεν είναι «μπορείς» αλλά «το έχεις σκεφτεί».' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Major life transitions */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Μεγάλες αλλαγές ζωής <span className="text-pink-500">και τι σημαίνουν για το timing</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Κάποιες αλλαγές φέρνουν ιδιαίτερη ορμή για τατουάζ. Αξίζει να ξέρεις τι λέει κάθε τύπος για την ετοιμότητά σου.
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { title: 'Μετακόμιση / νέο κεφάλαιο', desc: 'Ένα από τα πιο κλασικά timing για τατουάζ. Η φυσική αλλαγή χώρου δημιουργεί ανάγκη για ένα marker. Συνήθως αυτά τα τατουάζ γερνούν καλά.' },
                { title: 'Τέλος σχέσης ή οικογενειακή αλλαγή', desc: 'Τα τατουάζ αμέσως μετά από χωρισμό ή απώλεια μπορεί να λένε αυτό που θέλεις να πουν ή μπορεί να είναι αντίδραση. Λίγος χρόνος για να ξεχωρίσεις το ένα από το άλλο δεν βλάπτει.' },
                { title: 'Νέα αρχή: δουλειά, σχέση, τόπος', desc: 'Αν κάτι καλό μόλις άρχισε, ένα τατουάζ που το γιορτάζει έχει διαφορετικό βάρος από ένα που κλείνει κάτι παλιό. Αμφότερα είναι έγκυρα, αλλά είναι διαφορετικά.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Emotional readiness and mature decisions */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Συναισθηματική ετοιμότητα <span className="text-pink-500">και ώριμη απόφαση</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Δεν υπάρχει test για αυτό, αλλά υπάρχουν ερωτήματα που αξίζει να απαντήσεις πριν κλείσεις ραντεβού.
            </p>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Πόσο καιρό φέρεις αυτή την ιδέα;', desc: 'Η χρονική αντοχή μιας ιδέας λέει πολλά. Μια ιδέα που επιμένει 6+ μήνες έχει διαφορετικό βάρος από μια που ήρθε πριν από δύο εβδομάδες.' },
                { step: '02', title: 'Αλλάζει το σχέδιο συνεχώς ή έχει σταθεροποιηθεί;', desc: 'Αν κάθε μήνα αλλάζεις τελείως ιδέα για το τι θέλεις, ίσως χρειάζεσαι λίγο χρόνο ακόμα. Αν η βασική ιδέα μένει σταθερή και εξελίσσεται, αυτό είναι σημάδι ωριμότητας.' },
                { step: '03', title: 'Πώς σε φαντάζεσαι με αυτό στα 40, στα 50;', desc: 'Δεν είναι απαραίτητο να σε ενθουσιάζει η απάντηση, αλλά αν σε τρομάζει, αξίζει να το εξερευνήσεις. Πολλά τατουάζ γερνούν πολύ καλά μαζί με τον άνθρωπο που τα κάνει.' },
                { step: '04', title: 'Κάνεις τατουάζ για σένα ή για κάποιον άλλον;', desc: 'Δεν είναι απαραίτητα κακό αν υπάρχει κάποιος άλλος στην εξίσωση. Αλλά αξίζει να ξέρεις γιατί το κάνεις, γιατί αυτός είναι ο λόγος που θα το βλέπεις για χρόνια.' },
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-pink-600/30 shrink-0 w-12 text-center leading-none pt-1">{item.step}</div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Age and changing tastes */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Ηλικία και <span className="text-pink-500">αλλαγές γούστου</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Το πιο συχνό φόβο είναι: «και αν αλλάξω γνώμη;». Αξίζει να το σκεφτείς, αλλά όχι να σε παραλύει.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Το γούστο αλλάζει, και αυτό είναι εντάξει', desc: 'Τα περισσότερα τατουάζ που γίνονται με σκέψη δεν «μετανιώνονται» ακόμα και αν το γούστο αλλάξει. Αντιθέτως, γίνονται μέρος της ιστορίας σου.' },
                { title: 'Η ηλικία δεν είναι εμπόδιο', desc: 'Πολλοί κάνουν το πρώτο τους τατουάζ στα 30, 40 ή 50. Δεν υπάρχει «πρέπει να έχεις κάνει νωρίτερα», η απόφαση είναι σωστή όταν είναι δικιά σου.' },
                { title: 'Η ειλικρίνεια με τον εαυτό σου είναι το μόνο κριτήριο', desc: 'Αν η απόφαση είναι ειλικρινής, αν δεν πετάς από μια συναισθηματική κατάσταση, τότε έχεις ήδη απαντήσει στο ερώτημα.' },
                { title: 'Παλιές ιδέες που επιμένουν', desc: 'Αν μια ιδέα για τατουάζ συνοδεύει μια σκέψη για χρόνια και δεν έχει φύγει, δεν είναι impulsive. Είναι δοκιμασμένη. Μην την αγνοείς.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 210 approach */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Προσέγγιση <span className="text-pink-500">στο 210 Tattoo Athens</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Στο consultation δεν ρωτάμε μόνο «τι θέλεις», ρωτάμε και «γιατί τώρα». Όχι για να κρίνουμε, αλλά γιατί η απάντηση βοηθά να σχεδιαστεί κάτι που θα έχει νόημα για εσένα με τον χρόνο.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Δεν βιαζόμαστε να κλείσουμε ραντεβού', desc: 'Αν μετά τη συζήτηση φανεί ότι χρειάζεται λίγος χρόνος ακόμα, θα σου το πούμε ειλικρινά. Δεν βοηθά κανέναν ένα τατουάζ που γίνεται στην ακατάλληλη στιγμή.' },
                { title: 'Συζητάμε το context, όχι μόνο το σχέδιο', desc: 'Το «γιατί τώρα» και το «τι σημαίνει αυτό για σένα» είναι μέρος της διαδικασίας, βοηθάει στον σχεδιασμό ενός κομματιού με πραγματικό βάρος.' },
                { title: 'Ειλικρίνεια για παλιές ιδέες', desc: 'Αν φέρεις ιδέα για χρόνια και δεν έχεις κλείσει ποτέ, δεν θα σε ρωτήσουμε γιατί. Θα ξεκινήσουμε από εκεί.' },
                { title: 'Σεβασμός στην ταχύτητά σου', desc: 'Μερικοί ξέρουν τι θέλουν από την πρώτη επαφή. Άλλοι χρειάζονται δύο ή τρεις συζητήσεις. Και τα δύο είναι εντάξει.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Σκέφτεσαι τατουάζ αλλά δεν είσαι σίγουρος <span className="text-pink-500">αν η στιγμή είναι σωστή;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Στείλε μας μήνυμα στο Instagram του 210 Tattoo Athens. Μπορούμε να μιλήσουμε χωρίς δέσμευση, πολλές φορές μια συζήτηση αρκεί για να ξεκαθαρίσει αν είναι η κατάλληλη στιγμή ή όχι.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε Μήνυμα
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/best-time-for-tattoo-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Ποια Εποχή είναι Καλύτερη για Τατουάζ;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Φθινόπωρο, χειμώνας, άνοιξη ή καλοκαίρι: πρακτικός οδηγός για εποχή και aftercare.</p>
              </Link>
              <Link to="/blog/tattoo-artist-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πώς να Επιλέξεις τον Σωστό Tattoo Artist</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Τι να κοιτάς στο portfolio, πώς να αξιολογείς το consultation και γιατί ο artist που μιλά ειλικρινά κάνει τη διαφορά.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/cover-up-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Cover Up Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
